import { type RadarAddress, slugify } from '@sg/garnish';
import { type Address, type LocationStatus } from '@sg/graphql-schema';

import {
  findExistingDeliveryAddress,
  formatRadarAddress as formatDeliveryAddress,
  withoutCountryTerm as withoutRadarCountryTerm,
  type AddressDetails,
  type AddressWithoutIds,
} from '@order/features-locations';

import { type OrdersV2ForLocationQuery } from '../../../../../graphql/operations.generated';
import {
  activeCartDeliveryDetailsQuery,
  customerAddressesQuery,
  deliveryLocationByAddressIdQuery,
  deliveryLocationByAddressV2Query,
  recentLocationsWithDisclosureQuery,
} from '../../queries';

import type { CustomerAddressesQuery } from '../../graphql/CustomerDeliveryDetails.generated';
import type {
  DeliveryLocationByAddressIdQueryVariables,
  DeliveryLocationByAddressV2QueryVariables,
} from '../../graphql/DeliveryLocationByAddress.generated';
import type { DeliveryLocationWithoutNearbyStore, Location } from '../../LocationSearch.types';
import type { Client } from 'urql';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches delivery location details using coordinates + address.
 */
export async function getDeliveryLocationDetailsByAddress(
  params: GetDeliveryLocationDetailsByAddressParams,
): Promise<Location | DeliveryLocationWithoutNearbyStore> {
  const { client, address } = params;

  if (!address) {
    throw new Error('Failed to get delivery location details. No address passed in.');
  }

  const deliveryLocation =
    'addressId' in address
      ? await getDeliveryLocationByAddressId({
          client,
          input: { addressId: address.addressId },
        })
      : await getDeliveryLocationV2({
          client,
          input: {
            lat: address.latitude,
            lon: address.longitude,
            address1: address.street,
            city: address.city,
            state: address.state,
            zipcode: Number(address.zipCode),
          },
        });

  if (!deliveryLocation) {
    throw new Error('Failed to get delivery location details');
  }

  // ─── Delivery Location Without Nearby Store ──────────────────────────

  if (deliveryLocation.__typename === 'NoValidRestaurants') {
    return getWithoutNearbyStoreDeliveryLocationDetails(address);
  }

  // ─── Delivery Location With Nearby Store ─────────────────────────────

  return getWithNearbyStoreDeliveryLocationDetails({
    client,
    addressDetails: address,
    deliveryLocation,
  });
}

/**
 * Fetches delivery location details based on the provided address.
 */
export async function getDeliveryLocationDetailsByAddressId(
  params: GetDeliveryLocationDetailsByAddressIdParams,
): Promise<Location | DeliveryLocationWithoutNearbyStore> {
  const { client, addressDetails } = params;

  const deliveryLocation = await getDeliveryLocationByAddressId({
    client,
    input: { addressId: addressDetails.addressId },
  });

  if (!deliveryLocation) {
    throw new Error('Failed to get delivery location details');
  }

  // ─── Delivery Location Without Nearby Store ──────────────────────────

  if (deliveryLocation.__typename === 'NoValidRestaurants') {
    return getWithoutNearbyStoreDeliveryLocationDetails(addressDetails);
  }

  // ─── Delivery Location With Nearby Store ──────────────────────────

  return getWithNearbyStoreDeliveryLocationDetails({
    client,
    addressDetails,
    deliveryLocation,
  });
}

async function getDeliveryLocationV2(params: GetDeliveryLocationV2Params) {
  const { client, input } = params;

  const { data } = await deliveryLocationByAddressV2Query(client, { input });
  const { locationByAddressV2 } = data ?? {};

  if (
    locationByAddressV2?.__typename !== 'Location' &&
    locationByAddressV2?.__typename !== 'NoValidRestaurants'
  ) {
    throw new Error('Failed to get delivery location');
  }

  return locationByAddressV2;
}

async function getDeliveryLocationByAddressId(params: GetDeliveryLocationByAddressIdParams) {
  const { client, input } = params;

  const { data } = await deliveryLocationByAddressIdQuery(client, { input });
  const { locationByAddressId } = data ?? {};

  if (
    locationByAddressId?.__typename !== 'Location' &&
    locationByAddressId?.__typename !== 'NoValidRestaurants'
  ) {
    throw new Error('Failed to get delivery location');
  }

  return locationByAddressId;
}

/**
 * Fetches the active cart delivery location (if available).
 */
export async function getActiveCartDeliveryLocation(props: GetActiveCartDeliveryLocationProps) {
  const { client } = props;

  const { data } = await activeCartDeliveryDetailsQuery(client);
  const { cart } = data ?? {};

  // ─────────────────────────────────────────────────────────────────────

  const addressDetails = cart?.deliveryOrderDetail?.address;

  if (!addressDetails?.id) return;

  return getDeliveryLocationDetailsByAddressId({
    client,
    addressDetails: convertToAddressDetails(addressDetails),
  });
}

/**
 * Fetches the last interacted store delivery location (if available).
 */
export async function getLastInteractedStoreDeliveryLocation(
  props: GetLastInteractedStoreDeliveryLocation,
) {
  const { client, lastInteractedStoreAddressId } = props;

  const lastInteractedStore = await getCustomerDeliveryAddressByAddressId(
    client,
    lastInteractedStoreAddressId,
  );
  if (!lastInteractedStore) return;

  return getDeliveryLocationDetailsByAddressId({
    client,
    addressDetails: convertToAddressDetails(lastInteractedStore),
  });
}

/**
 * Attempts to get the last placed order delivery address ID.
 */
export async function getLastPlacedOrderDeliveryLocation(
  props: GetLastPlacedOrderDeliveryLocation,
) {
  const { client } = props;

  const { data: customerData } = await customerAddressesQuery(client);
  const isSignedInCustomer = customerData?.currentCustomer?.id !== undefined;

  // NOTE: We only query the last placed order only for signed-in customers.
  if (!isSignedInCustomer) return;

  const customerDeliveryAddress = await getCustomerDeliveryAddresses(client);

  const { data } = await recentLocationsWithDisclosureQuery(client, 'cache-first');
  const { ordersV2: ordersData } = data ?? {};

  const hasLastPlacedOrder = ordersData?.__typename === 'OrdersResponseSuccess';
  const lastPlacedOrderDetails = hasLastPlacedOrder
    ? findLastPlacedValidDeliveryOrder(ordersData.orders, customerDeliveryAddress) // prettier-ignore
    : undefined;

  if (!lastPlacedOrderDetails?.customerDeliveryAddress) return;

  return getDeliveryLocationDetailsByAddressId({
    client,
    addressDetails: convertToAddressDetails(lastPlacedOrderDetails.customerDeliveryAddress), // prettier-ignore
  });
}

/**
 * Attempts to get the customer first previously saved delivery location.
 */
export async function getCustomerFirstDeliveryLocation(props: GetCustomerFirstDeliveryLocation) {
  const { client } = props;

  const customerDeliveryAddresses = await getCustomerDeliveryAddresses(client);

  const customerFirstDeliveryAddress = customerDeliveryAddresses?.[0];

  if (!customerFirstDeliveryAddress) return;

  return getDeliveryLocationDetailsByAddressId({
    client,
    addressDetails: convertToAddressDetails(customerFirstDeliveryAddress),
  });
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Attempts to get customer's delivery addresses.
 */
export async function getCustomerDeliveryAddresses(client: Client) {
  const { data } = await customerAddressesQuery(client);

  return data?.currentCustomer?.addresses ?? [];
}

/**
 * Attempts to get the customer's delivery address based on the provided address ID.
 */
export async function getCustomerDeliveryAddressByAddressId(client: Client, addressId: string) {
  const customerAddresses = await getCustomerDeliveryAddresses(client);

  return customerAddresses.find((customerAddress) => customerAddress.id === addressId);
}

/**
 * Attempts to find a previously placed delivery order.
 */
function findLastPlacedValidDeliveryOrder(
  lastPlacedOrders: LastPlacedOrders,
  customDeliveryAddresses: CustomerDeliveryAddresses,
) {
  const customDeliveryAddressesSet = new Set(customDeliveryAddresses.map(({ id }) => id));

  const lastPlacedOrder = lastPlacedOrders.find(({ deliveryOrderDetail }) => {
    const deliveryAddressId = deliveryOrderDetail?.address?.id;

    return (
      deliveryAddressId !== undefined &&
      typeof deliveryAddressId === 'string' &&
      customDeliveryAddressesSet.has(deliveryAddressId)
    );
  });

  return {
    lastPlacedOrder: lastPlacedOrder,
    customerDeliveryAddress: customDeliveryAddresses.find(
      ({ id }) => id === lastPlacedOrder?.deliveryOrderDetail?.address?.id,
    ),
  };
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns delivery location (WITH nearby store) details based on the provided
 * address.
 */
async function getWithNearbyStoreDeliveryLocationDetails(
  props: GetWithNearbyStoreDeliveryLocationDetails,
): Promise<Location> {
  const { client, addressDetails, deliveryLocation } = props;

  const customerAddresses = await getCustomerDeliveryAddresses(client);
  const existingDeliveryAddress = findExistingDeliveryAddress(customerAddresses, addressDetails);

  const deliveryAddressName = existingDeliveryAddress?.name;
  const deliveryAddressId = existingDeliveryAddress?.id;

  // ─────────────────────────────────────────────────────────────────────

  return extractDeliveryLocationDetails({
    deliveryLocation,
    deliveryAddressId,
    deliveryAddressName,
    address: addressDetails,
  });
}

/**
 * Returns delivery location (WITH nearby store) details based on the provided
 * address.
 */
export function extractDeliveryLocationDetails(
  params: ExtractDeliveryLocationDetailsParams,
): Location {
  const { address, deliveryAddressId, deliveryAddressName, deliveryLocation } = params;

  const { longitude, latitude, city, state, country, zipCode, street } = address;

  const estimatedDeliveryTime = getDeliveryLocationETA(deliveryLocation);
  const deliveryFormattedAddress = formatDeliveryAddress(address);
  const isDeliveryLocationClosed = deliveryLocation?.status === 'CLOSED';

  const deliveryLocationRestaurant = deliveryLocation?.restaurant ?? {};
  const deliveryLocationRestaurantWithDisclosure =
    deliveryLocationRestaurant as unknown as ValidDeliveryLocation & {
      showDeliveryPriceDifferenciationDisclosure: boolean;
    };

  const {
    id: restaurantId,
    name: restaurantName,
    slug: restaurantSlug,
    flexMessage,
    phone,
    deliveryFee,
  } = deliveryLocationRestaurant;
  const { name: deliveryVendorName, restaurantId: deliveryVendorRestaurantId } =
    deliveryLocation.vendor;

  // ─────────────────────────────────────────────────────────────────────

  return {
    isDelivery: true,
    id: slugify(deliveryFormattedAddress),
    addressId: deliveryAddressId ?? '',
    name: deliveryAddressName ?? '',
    address: deliveryFormattedAddress ?? '',
    estimatedDeliveryTime,
    flexMessage,
    phone,
    isClosed: isDeliveryLocationClosed,
    lat: latitude,
    lng: longitude,
    street,
    zipCode,
    city,
    state,
    country,
    restaurantSlug,
    restaurantName,
    restaurantId,
    restaurantDeliveryFee: deliveryFee,
    deliveryVendorName,
    deliveryVendorRestaurantId,
    showDeliveryPriceDifferenciationDisclosure:
      deliveryLocationRestaurantWithDisclosure.showDeliveryPriceDifferenciationDisclosure ?? false,
  };
}

/**
 * Returns delivery location (WITHOUT nearby store) details based on the provided
 * address.
 */
function getWithoutNearbyStoreDeliveryLocationDetails(
  address: AddressDetails | AddressWithoutIds,
): DeliveryLocationWithoutNearbyStore {
  const deliveryFormattedAddress = formatDeliveryAddress(address);

  return {
    id: slugify(deliveryFormattedAddress),
    lat: address.latitude,
    lng: address.longitude,
    street: address.street,
    zipCode: address.zipCode,
    city: address.city,
    state: address.state,
    country: address.country,
    address: deliveryFormattedAddress,
    name: '',
    restaurantSlug: undefined,
    restaurantId: '',
    restaurantName: '',
    addressId: undefined,
    deliveryVendorName: undefined,
    deliveryVendorRestaurantId: undefined,
    restaurantDeliveryFee: undefined,
  };
}

/**
 * Returns a formatter function for Radar Autocomplete Predictions
 */
export function formatRadarDeliveryAddressPrediction(
  customerDeliveryAddresses: CustomerDeliveryAddresses,
) {
  return function (predictionAddress: RadarAddress) {
    const {
      latitude,
      longitude,
      addressLabel: street,
      city,
      stateCode: state,
      postalCode: zipCode,
      countryCode: country,
      formattedAddress,
    } = predictionAddress;

    const existingDeliveryAddress = findExistingDeliveryAddress(customerDeliveryAddresses, {
      street,
      city,
      state,
      zipCode,
      latitude,
      longitude,
      country,
      formattedAddress,
    });

    const id = existingDeliveryAddress?.id ?? slugify(formattedAddress);
    const name = existingDeliveryAddress?.name ?? '';

    const address = withoutRadarCountryTerm(predictionAddress);

    return {
      id,
      name,
      address,
      latitude,
      longitude,
      street,
      city,
      state,
      zipCode,
      country,
    };
  };
}

/**
 * Returns estimated delivery time for the provided delivery location.
 */
function getDeliveryLocationETA(deliveryLocation: MaybeDeliveryLocationWithETA) {
  if (!deliveryLocation?.estimateRange) return;

  const { start, end } = deliveryLocation.estimateRange;

  return start === end ? `${start}` : `${start}-${end}`;
}

function convertToAddressDetails(customerDeliveryAddress: Address): AddressDetails {
  return {
    ...customerDeliveryAddress,
    formattedAddress: formatDeliveryAddress(customerDeliveryAddress),
    addressId: customerDeliveryAddress.id,
    name: customerDeliveryAddress.name ?? '',
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type GetDeliveryLocationDetailsByAddressIdParams = {
  client: Client;
  addressDetails: AddressDetails;
};

type GetDeliveryLocationDetailsByAddressParams = {
  client: Client;
  address: AddressDetails | AddressWithoutIds | undefined;
};

type GetDeliveryLocationV2Params = {
  client: Client;
  input: DeliveryLocationByAddressV2QueryVariables['input'];
};

type GetDeliveryLocationByAddressIdParams = {
  client: Client;
  input: DeliveryLocationByAddressIdQueryVariables['input'];
};

type GetActiveCartDeliveryLocationProps = Readonly<{
  client: Client;
}>;

type GetLastInteractedStoreDeliveryLocation = Readonly<{
  client: Client;
  lastInteractedStoreAddressId: string;
}>;

type GetLastPlacedOrderDeliveryLocation = Readonly<{
  client: Client;
}>;

type GetCustomerFirstDeliveryLocation = Readonly<{
  client: Client;
}>;

type GetWithNearbyStoreDeliveryLocationDetails = Readonly<{
  client: Client;
  addressDetails: AddressDetails | AddressWithoutIds;
  deliveryLocation: ValidDeliveryLocation;
}>;

type ExtractDeliveryLocationDetailsParams = {
  deliveryAddressId: string | undefined;
  deliveryAddressName: string | undefined | null;
  deliveryLocation: Omit<ValidDeliveryLocation, '__typename' | 'status' | 'estimateRange'> & {
    status?: LocationStatus;
    estimateRange?: { start: number; end: number };
  };
  address: Omit<AddressWithoutIds, 'formattedAddress'> &
    Readonly<{ formattedAddress?: string; name?: string | null }>;
};

type ValidDeliveryLocation = Extract<
  Awaited<ReturnType<typeof getDeliveryLocationByAddressId>>,
  Readonly<{ __typename: 'Location' }>
>;

type CustomerDeliveryAddresses = NonNullable<
  CustomerAddressesQuery['currentCustomer']
>['addresses'];

type MaybeDeliveryLocationWithETA = Readonly<{
  estimateRange?: {
    start: number;
    end: number;
  };
}>;

type LastPlacedOrders = Extract<
  OrdersV2ForLocationQuery['ordersV2'],
  Readonly<{ __typename: 'OrdersResponseSuccess' }>
>['orders'];
